import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// ============================================================================
// BRAND CONFIGURATION - Easy to customize for white-labeling
// ============================================================================
export interface BrandConfig {
  name: string;
  shortName: string;
  tagline: string;
  logo?: string;
  version: string;
}

// ============================================================================
// THEME CONFIGURATION TYPES
// ============================================================================
export interface ThemeColors {
  // Primary colors
  primary: string;
  primaryDark: string;
  primaryLight: string;

  // Secondary/Accent colors
  secondary: string;
  secondaryDark: string;
  secondaryLight: string;

  // Sidebar colors
  sidebarBg: string;
  sidebarBgGradient: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarActiveItem: string;
  sidebarHoverItem: string;
  /**
   * The active-item rail and icon tint. Kept as its own token rather than
   * reusing `info`: the sidebar sits on a dark surface where `info` (tuned for
   * light backgrounds) is often too dim to read as "selected". Each preset
   * supplies a value that is legible against its own sidebar.
   */
  sidebarAccent: string;
  /** Gradient form of `sidebarAccent`, used for the active rail and the mark. */
  sidebarAccentGradient: string;

  // Header colors
  headerBg: string;
  headerBgGradient: string;
  headerText: string;

  // Background colors
  background: string;
  backgroundPaper: string;
  backgroundAlt: string;

  // Text colors
  textPrimary: string;
  textSecondary: string;

  // Status colors
  success: string;
  warning: string;
  error: string;
  info: string;

  // Other
  divider: string;
  border: string;
}

export interface ThemeConfig {
  id: string;
  name: string;
  colors: ThemeColors;
  brand: BrandConfig;
}

// ============================================================================
// PRESET THEMES - Add more themes here for white-labeling
// ============================================================================

const defaultBrand: BrandConfig = {
  name: 'Stratify',
  shortName: 'Stratify',
  tagline: 'The structured ERP for growing businesses',
  logo: '/logo192.png',
  version: '1.0.0',
};

/**
 * Stratify — the brand theme, and the default.
 *
 * These values are the app-token spelling of `pages/landing/landingTokens.ts`,
 * so the authenticated shell reads as the same product as the landing page and
 * the login screen a user just came through. They are duplicated rather than
 * imported: `landingTokens` deliberately does not depend on the theme system
 * (a logged-out visitor must always see Stratify brand regardless of whatever
 * preset sits in their localStorage), and importing it here would create the
 * cycle that separation exists to avoid.
 *
 * Keep the two in sync by hand if the brand palette moves. The shared values
 * are ink #070912 and the single-hue #6fa3ee -> #3e6bc4 strata ramp.
 */
export const stratifyTheme: ThemeConfig = {
  id: 'stratify',
  name: 'Stratify',
  brand: defaultBrand,
  colors: {
    // #215fbe rather than a brighter tint: primary lands on white surfaces as
    // button and link text, where anything much lighter falls under WCAG AA.
    // The lighter tint is reserved for dark surfaces and fills.
    primary: '#215fbe',
    primaryDark: '#173f80',
    primaryLight: '#5b8fe0',

    secondary: '#127c7c',
    secondaryDark: '#0b5c5c',
    secondaryLight: '#4fadad',

    sidebarBg: '#070912',
    sidebarBgGradient: 'linear-gradient(180deg, #070912 0%, #10162a 50%, #070912 100%)',
    sidebarText: '#f4f6fb',
    sidebarTextMuted: 'rgba(226,232,240,0.68)',
    sidebarActiveItem: 'rgba(111,163,238,0.14)',
    sidebarHoverItem: 'rgba(255,255,255,0.05)',
    sidebarAccent: '#6fa3ee',
    sidebarAccentGradient: 'linear-gradient(180deg, #6fa3ee 0%, #3e6bc4 100%)',

    headerBg: '#070912',
    headerBgGradient: 'linear-gradient(135deg, #0c1020 0%, #070912 55%, #10162a 100%)',
    headerText: '#f4f6fb',

    background: '#f5f7fb',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#eef2fa',

    textPrimary: '#101828',
    textSecondary: '#55607a',

    success: '#15803d',
    warning: '#b45309',
    error: '#b91c1c',
    info: '#215fbe',

    divider: '#e2e8f0',
    border: '#e2e8f0',
  },
};

// Blue Theme (Current - Professional)
export const blueTheme: ThemeConfig = {
  id: 'blue',
  name: 'Professional Blue',
  brand: defaultBrand,
  colors: {
    primary: '#1976d2',
    primaryDark: '#1565c0',
    primaryLight: '#42a5f5',

    secondary: '#6d5bd0',
    secondaryDark: '#5643b8',
    secondaryLight: '#9082e0',

    sidebarBg: '#0d2b57',
    sidebarBgGradient: 'linear-gradient(180deg, #0d2b57 0%, #123a70 50%, #0d2b57 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(255, 255, 255, 0.15)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#e3f2fd',
    sidebarAccentGradient: 'linear-gradient(180deg, #ffffff 0%, #bbdefb 100%)',

    headerBg: '#1976d2',
    headerBgGradient: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
    headerText: '#ffffff',

    background: '#f5f7fa',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#e3f2fd',

    textPrimary: '#212121',
    textSecondary: '#757575',

    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    divider: '#e0e0e0',
    border: '#e0e0e0',
  },
};

// Dark Theme
export const darkTheme: ThemeConfig = {
  id: 'dark',
  name: 'Dark Mode',
  brand: defaultBrand,
  colors: {
    primary: '#90caf9',
    primaryDark: '#42a5f5',
    primaryLight: '#e3f2fd',

    secondary: '#ce93d8',
    secondaryDark: '#ab47bc',
    secondaryLight: '#f3e5f5',

    sidebarBg: '#1e1e1e',
    sidebarBgGradient: 'linear-gradient(180deg, #1e1e1e 0%, #2d2d2d 50%, #1e1e1e 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(144, 202, 249, 0.2)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#90caf9',
    sidebarAccentGradient: 'linear-gradient(180deg, #90caf9 0%, #64b5f6 100%)',

    headerBg: '#272727',
    headerBgGradient: 'linear-gradient(135deg, #272727 0%, #1e1e1e 100%)',
    headerText: '#ffffff',

    background: '#121212',
    backgroundPaper: '#1e1e1e',
    backgroundAlt: '#2d2d2d',

    textPrimary: '#ffffff',
    textSecondary: '#b0b0b0',

    success: '#66bb6a',
    warning: '#ffa726',
    error: '#ef5350',
    info: '#29b6f6',

    divider: '#424242',
    border: '#424242',
  },
};

// Green Theme (Eco/Nature)
export const greenTheme: ThemeConfig = {
  id: 'green',
  name: 'Nature Green',
  brand: defaultBrand,
  colors: {
    primary: '#2e7d32',
    primaryDark: '#1b5e20',
    primaryLight: '#4caf50',

    secondary: '#00897b',
    secondaryDark: '#00695c',
    secondaryLight: '#26a69a',

    sidebarBg: '#1b5e20',
    sidebarBgGradient: 'linear-gradient(180deg, #1b5e20 0%, #2a6b2e 50%, #1b5e20 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(255, 255, 255, 0.15)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#ffffff',
    sidebarAccentGradient: 'linear-gradient(180deg, #ffffff 0%, #c8e6c9 100%)',

    headerBg: '#2e7d32',
    headerBgGradient: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 50%, #1b5e20 100%)',
    headerText: '#ffffff',

    background: '#f1f8e9',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#c8e6c9',

    textPrimary: '#212121',
    textSecondary: '#757575',

    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    divider: '#c8e6c9',
    border: '#a5d6a7',
  },
};

// Purple Theme (Creative)
export const purpleTheme: ThemeConfig = {
  id: 'purple',
  name: 'Creative Purple',
  brand: defaultBrand,
  colors: {
    primary: '#7b1fa2',
    primaryDark: '#4a148c',
    primaryLight: '#ab47bc',

    secondary: '#e91e63',
    secondaryDark: '#c2185b',
    secondaryLight: '#f06292',

    sidebarBg: '#4a148c',
    sidebarBgGradient: 'linear-gradient(180deg, #4a148c 0%, #5e1a9e 50%, #4a148c 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(255, 255, 255, 0.15)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#ce93d8',
    sidebarAccentGradient: 'linear-gradient(180deg, #e1bee7 0%, #ba68c8 100%)',

    headerBg: '#7b1fa2',
    headerBgGradient: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)',
    headerText: '#ffffff',

    background: '#f3e5f5',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#e1bee7',

    textPrimary: '#212121',
    textSecondary: '#757575',

    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    divider: '#e1bee7',
    border: '#ce93d8',
  },
};

// Orange Theme (Energy)
export const orangeTheme: ThemeConfig = {
  id: 'orange',
  name: 'Energy Orange',
  brand: defaultBrand,
  colors: {
    primary: '#f57c00',
    primaryDark: '#e65100',
    primaryLight: '#ffb74d',

    secondary: '#0288d1',
    secondaryDark: '#01579b',
    secondaryLight: '#4fc3f7',

    sidebarBg: '#e65100',
    // Final stop pulled from #f57c00 to #e65100. At #f57c00 even pure white
    // reaches only 2.70:1, so this preset's own white nav labels failed AA at
    // the bottom of the panel regardless of the active-item treatment.
    sidebarBgGradient: 'linear-gradient(180deg, #bf360c 0%, #cf4200 50%, #bf360c 100%)',
    sidebarText: '#ffffff',
    sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
    sidebarActiveItem: 'rgba(255, 255, 255, 0.15)',
    sidebarHoverItem: 'rgba(255, 255, 255, 0.08)',
    sidebarAccent: '#ffffff',
    sidebarAccentGradient: 'linear-gradient(180deg, #ffffff 0%, #ffe0b2 100%)',

    headerBg: '#f57c00',
    headerBgGradient: 'linear-gradient(135deg, #f57c00 0%, #e65100 50%, #bf360c 100%)',
    headerText: '#ffffff',

    background: '#fff3e0',
    backgroundPaper: '#ffffff',
    backgroundAlt: '#ffe0b2',

    textPrimary: '#212121',
    textSecondary: '#757575',

    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',

    divider: '#ffe0b2',
    border: '#ffcc80',
  },
};

// All available themes
export const themes: ThemeConfig[] = [
  stratifyTheme,
  blueTheme,
  darkTheme,
  greenTheme,
  purpleTheme,
  orangeTheme,
];

// ============================================================================
// CREATE MUI THEME FROM CONFIG
// ============================================================================
/**
 * Elevation scale. Restrained on purpose — a layered, low-opacity shadow
 * reads as "structured surface" on a light background; a single heavy shadow
 * reads as a sticker floating above the page. Dark mode gets its own, softer
 * scale (see `createAppTheme`) since a black shadow on a dark surface just
 * muddies it instead of lifting it.
 */
const elevation = {
  low: '0 1px 2px rgba(16,24,40,0.06)',
  medium: '0 1px 2px rgba(16,24,40,0.04), 0 4px 12px rgba(16,24,40,0.08)',
  high: '0 4px 8px rgba(16,24,40,0.04), 0 12px 28px rgba(16,24,40,0.12)',
};

const elevationDark = {
  low: '0 1px 2px rgba(0,0,0,0.3)',
  medium: '0 2px 6px rgba(0,0,0,0.35), 0 8px 20px rgba(0,0,0,0.3)',
  high: '0 4px 10px rgba(0,0,0,0.4), 0 16px 32px rgba(0,0,0,0.4)',
};

export const createAppTheme = (config: ThemeConfig): Theme => {
  const { colors } = config;
  const isDark = config.id === 'dark';
  const shadow = isDark ? elevationDark : elevation;

  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: colors.primary,
        dark: colors.primaryDark,
        light: colors.primaryLight,
      },
      secondary: {
        main: colors.secondary,
        dark: colors.secondaryDark,
        light: colors.secondaryLight,
      },
      success: {
        main: colors.success,
      },
      warning: {
        main: colors.warning,
      },
      error: {
        main: colors.error,
      },
      info: {
        main: colors.info,
      },
      background: {
        default: colors.background,
        paper: colors.backgroundPaper,
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary,
      },
      divider: colors.divider,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700, fontSize: '2.5rem', lineHeight: 1.15, letterSpacing: '-0.02em' },
      h2: { fontWeight: 700, fontSize: '2rem', lineHeight: 1.2, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700, fontSize: '1.625rem', lineHeight: 1.25, letterSpacing: '-0.015em' },
      h4: { fontWeight: 700, fontSize: '1.375rem', lineHeight: 1.3, letterSpacing: '-0.01em' },
      h5: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.35, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.4 },
      subtitle1: { fontWeight: 600, fontSize: '0.9375rem' },
      subtitle2: { fontWeight: 600, fontSize: '0.8125rem' },
      body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
      button: { fontWeight: 600, textTransform: 'none' as const },
      caption: { fontSize: '0.75rem' },
      overline: { fontWeight: 600, letterSpacing: '0.06em' },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Visible, on-brand focus ring for every interactive element — a
          // browser default outline is easy to lose against a dark sidebar
          // and easy to accidentally suppress; this keeps it explicit and
          // consistent instead.
          '*:focus-visible': {
            outline: `2px solid ${colors.primary}`,
            outlineOffset: '1px',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '*': {
              animationDuration: '0.001ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.001ms !important',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
            paddingInline: 16,
          },
          sizeSmall: { paddingInline: 12 },
          containedPrimary: {
            '&:hover': { boxShadow: shadow.low },
          },
          outlined: {
            borderColor: colors.border,
            '&:hover': { borderColor: colors.primary, backgroundColor: `${colors.primary}0d` },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 10,
            border: `1px solid ${colors.divider}`,
            boxShadow: 'none',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { borderRadius: 10, backgroundImage: 'none' },
          elevation1: { boxShadow: shadow.low },
          elevation2: { boxShadow: shadow.medium },
          elevation3: { boxShadow: shadow.medium },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: { size: 'small' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.875rem' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: 999,
          },
          sizeSmall: { fontSize: '0.6875rem', height: 22 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#1e293b' : '#0f172a',
            fontSize: '0.75rem',
            fontWeight: 500,
            borderRadius: 6,
            padding: '6px 10px',
          },
          arrow: { color: isDark ? '#1e293b' : '#0f172a' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            boxShadow: shadow.high,
            backgroundImage: 'none',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: 700, fontSize: '1.125rem', padding: '20px 24px 12px' },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '12px 24px' },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '16px 24px 20px', gap: 8 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontSize: '0.8125rem',
          },
          standardSuccess: { backgroundColor: isDark ? undefined : '#f0fdf4' },
          standardWarning: { backgroundColor: isDark ? undefined : '#fffbeb' },
          standardError: { backgroundColor: isDark ? undefined : '#fef2f2' },
          standardInfo: { backgroundColor: isDark ? undefined : `${colors.primary}0d` },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { height: 2.5, borderRadius: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 40,
            '&.Mui-selected': { color: colors.primary },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 10, boxShadow: shadow.medium },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            marginInline: 4,
            fontSize: '0.875rem',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: 8 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: colors.divider },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: colors.divider },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 700, fontSize: '0.65rem' },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 999, height: 6 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { borderRadius: 10, boxShadow: shadow.medium },
        },
      },
    },
  });
};

// ============================================================================
// THEME CONTEXT
// ============================================================================
interface ThemeContextType {
  currentTheme: ThemeConfig;
  setTheme: (themeId: string) => void;
  availableThemes: ThemeConfig[];
  colors: ThemeColors;
  brand: BrandConfig;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// ============================================================================
// THEME PROVIDER COMPONENT
// ============================================================================
interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'stratify'
}) => {
  const [currentThemeId, setCurrentThemeId] = useState(() => {
    // Try to get saved theme from localStorage
    const saved = localStorage.getItem('app-theme');
    return saved || defaultTheme;
  });

  const currentTheme = useMemo(() => {
    return themes.find(t => t.id === currentThemeId) || stratifyTheme;
  }, [currentThemeId]);

  const muiTheme = useMemo(() => {
    return createAppTheme(currentTheme);
  }, [currentTheme]);

  const setTheme = (themeId: string) => {
    setCurrentThemeId(themeId);
    localStorage.setItem('app-theme', themeId);
  };

  const contextValue: ThemeContextType = {
    currentTheme,
    setTheme,
    availableThemes: themes,
    colors: currentTheme.colors,
    brand: currentTheme.brand,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// ============================================================================
// HOOK TO USE THEME
// ============================================================================
export const useAppTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within a ThemeProvider');
  }
  return context;
};

// Export default theme for backward compatibility
export default createAppTheme(stratifyTheme);
